import { useCallback, useState } from 'react';

import {
  currentUtcMonth,
  formatProUntil,
  isProPickerEligible,
  proErrorMessage,
  scanUsage,
  userPro,
} from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import type { GroupResponseDto } from '@splitfiesta/shared';

import { GroupSelectorModal } from '@/components/home/GroupSelectorModal';
import { ProGroupDialogContainer } from '@/components/pro/ProGroupDialogContainer';
import { proFallbackMessage } from '@/components/pro/proFallbackMessage';

import { useFeatureFlag } from '@/hooks/queries/useFeatureFlagsQuery';
import {
  useGroupWithDashboardQuery,
  useGroupsWithBalancesQuery,
} from '@/hooks/queries/useGroups';
import {
  useProGroupMutations,
  useProGroupsQuery,
} from '@/hooks/queries/useProGroups';
import type { ApiError } from '@/hooks/useApi';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';

import { useAuthStore } from '@/stores/authStore';

import { toast } from '@/utils/toast';

import { ProSection } from './ProSection';

/** Pro state comes from the auth store; no profile query is added. */
export function ProSectionContainer() {
  const { t, i18n } = useTranslation();
  const user = useAuthStore(state => state.user);
  const proEnabled = useFeatureFlag('pro');
  // The confirm and offline copy live in `groups`, which Settings does not load.
  const groupsReady = useNamespace('groups');
  const { isOnline } = useNetworkStatus();
  const pro = userPro(user);
  const visible = proEnabled && pro.active;
  const { data, isLoading, isError, isFetching, refetch } =
    useProGroupsQuery(visible);
  const { remove } = useProGroupMutations();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickedId, setPickedId] = useState<string | null>(null);
  // The dialog opens only while the user waits on the pick; after a failure
  // it takes a Retry, so a background refetch cannot open it unprompted.
  const [awaitingPick, setAwaitingPick] = useState(false);
  // The dialog names who made the group Pro from the dashboard's balances,
  // which a groups-list row does not carry.
  const {
    data: pickedGroup,
    isError: pickFailed,
    isFetching: pickFetching,
    refetch: refetchPick,
  } = useGroupWithDashboardQuery(pickedId ?? '', !!pickedId);
  const pickLoaded = !!pickedId && pickedGroup?.id === pickedId;
  if (awaitingPick && pickFailed && !pickFetching && !pickLoaded) {
    setAwaitingPick(false);
  }
  // Stable, so the picker's keyboard focus survives a re-render.
  const pickable = useCallback(
    (group: GroupResponseDto) =>
      isProPickerEligible({ proEnabled, user, group }),
    [proEnabled, user]
  );
  // The picker's own unsearched list, so this shares its cache. Like mobile,
  // the entry stays hidden until a group it would offer is known.
  const { data: allGroups } = useGroupsWithBalancesQuery(undefined, visible);
  const hasPickableGroup = (allGroups ?? []).some(pickable);

  if (!visible || !groupsReady) return null;

  const handleRemove = async (groupId: string) => {
    if (!isOnline) return;
    try {
      await remove.mutateAsync(groupId);
    } catch (error) {
      toast.error(
        proErrorMessage(error as ApiError, t, i18n.language, other =>
          proFallbackMessage(other, t)
        )
      );
    }
  };

  return (
    <>
      <ProSection
        pro={pro}
        untilLabel={formatProUntil(pro.until, i18n.language)}
        proGroups={data?.items ?? []}
        loadingGroups={isLoading}
        groupsFailed={isError && !data}
        retryingGroups={isFetching}
        onRetry={() => void refetch()}
        onRemove={groupId => void handleRemove(groupId)}
        busyGroupId={remove.isPending ? remove.variables : undefined}
        isOnline={isOnline}
        scansIncluded={
          scanUsage({
            flagOn: proEnabled,
            user,
            nowMonth: currentUtcMonth(),
          }).kind === 'pro'
        }
        onMakePro={
          pro.slots.used < pro.slots.total && hasPickableGroup
            ? () => {
                setPickedId(null);
                setAwaitingPick(false);
                setPickerOpen(true);
              }
            : undefined
        }
        pick={
          pickedId && !(awaitingPick && pickLoaded)
            ? {
                failed: !awaitingPick,
                retrying: pickFetching,
                onRetry: () => {
                  setAwaitingPick(true);
                  void refetchPick();
                },
              }
            : undefined
        }
      />
      {pickerOpen && (
        <GroupSelectorModal
          groups={[]}
          title={t('settings:pro.pickGroupTitle')}
          notice={null}
          filter={pickable}
          onClose={() => setPickerOpen(false)}
          onSelectGroup={group => {
            setPickerOpen(false);
            setPickedId(group.id);
            setAwaitingPick(true);
          }}
        />
      )}
      {awaitingPick && pickLoaded && (
        <ProGroupDialogContainer
          isOpen
          onClose={() => {
            setPickedId(null);
            setAwaitingPick(false);
          }}
          group={pickedGroup}
        />
      )}
    </>
  );
}
